import {type EChartProps} from '@hm/fundamental-echarts'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, GRID_LINE_COLOR, SERIES_COLORS, TOOLTIP_BG} from '../consts'
import {DifficultyFilter, ValueDisplayMode, YearlyHierarchyAnalysisDataPoint} from '../types'
import {
  buildMarkLineConfig,
  buildPerNodeYearlyDifficulty,
  buildYearlySeriesData,
  computeBoundariesFromData,
  createTooltipFormatter
} from '../utils'

type HierarchyEntry = {id: string; name: string}

export type UseYearlyChartOptionParams = {
  yearlyData: YearlyHierarchyAnalysisDataPoint[]
  years: number[]
  hierarchyEntries: HierarchyEntry[]
  effectiveVisibleIds: string[]
  difficultyFilter: DifficultyFilter
  valueDisplayMode: ValueDisplayMode
  enabled?: boolean
}

export const useYearlyChartOption = ({
  yearlyData,
  years,
  hierarchyEntries,
  effectiveVisibleIds,
  difficultyFilter,
  valueDisplayMode,
  enabled = true
}: UseYearlyChartOptionParams): EChartProps['option'] => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return useMemo(() => {
    if (!enabled) return {}

    const currentYear = new Date().getFullYear()
    const isPercentage = valueDisplayMode === 'percentage'
    const isSingleDifficulty = difficultyFilter !== 'all'
    const rawBoundaries = computeBoundariesFromData(yearlyData)
    const boundaries = isSingleDifficulty ? undefined : rawBoundaries

    const maxQuestionCount = yearlyData.reduce(
      (max, dp) => Math.max(max, dp.totalScore?.questionCount ?? 0),
      0
    )

    const basicLabel = t('comparativeAnalysis.card.basic')
    const advancedLabel = t('comparativeAnalysis.card.advanced')
    const excellenceLabel = t('comparativeAnalysis.card.excellence')

    const decShort = t('comparativeAnalysis.chart.months.dec')
    const todayLabel = t('comparativeAnalysis.chart.today')

    // X-axis: "Dec. 2020", ..., "Today" for current year
    const xAxisLabels = years.map((y) =>
      y === currentYear ? todayLabel : `${decShort}. ${y}`
    )

    const gridSeries = {
      name: '',
      type: 'line',
      data: years.map(() => null),
      smooth: true,
      emphasis: {focus: 'none'},
      zlevel: 0,
      ...(isPercentage ? {} : buildMarkLineConfig(boundaries, maxQuestionCount))
    }

    const allDataSeries = buildYearlySeriesData(
      hierarchyEntries,
      yearlyData,
      years,
      valueDisplayMode
    )
    const dataSeries = allDataSeries.filter((_, i) =>
      effectiveVisibleIds.includes(hierarchyEntries[i].id)
    )

    // Y-axis max: highest actual plotted value + 10% headroom
    const maxDataValue = dataSeries.reduce((max, s) => {
      const seriesMax = (s.data as (number | null)[]).reduce<number>(
        (m, v) => (v !== null && v > m ? v : m),
        0
      )
      return Math.max(max, seriesMax)
    }, 0)
    const yAxisMax = isPercentage ? 100 : maxDataValue > 0 ? Math.ceil(maxDataValue * 1.1) : undefined

    const series = [gridSeries, ...dataSeries]

    const visibleEntries = hierarchyEntries.filter((e) => effectiveVisibleIds.includes(e.id))
    const perNodeDifficulty = buildPerNodeYearlyDifficulty(visibleEntries, yearlyData, years)

    const colorMap = new Map(
      hierarchyEntries.map((e, i) => [e.name, SERIES_COLORS[i % SERIES_COLORS.length]])
    )

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: TOOLTIP_BG,
        borderColor: TOOLTIP_BG,
        textStyle: {color: '#FFFFFF'},
        formatter: createTooltipFormatter({
          basicLabel,
          advancedLabel,
          excellenceLabel,
          perNodeDifficulty,
          difficultyFilter,
          valueDisplayMode,
          colorMap,
          getTitle: (dataIndex) => {
            const y = years[dataIndex]
            return y === currentYear ? todayLabel : `${decShort}. ${y}`
          }
        })
      },
      legend: {show: false},
      grid: {
        left: !isPercentage && boundaries ? 30 : 16,
        right: 16,
        top: 16,
        bottom: 8,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisLabels,
        boundaryGap: false,
        axisLabel: {interval: 0}
      },
      yAxis: [
        {
          type: 'value',
          name: isPercentage
            ? t('comparativeAnalysis.chart.yAxisLabelPercentage')
            : t('comparativeAnalysis.chart.yAxisLabel'),
          min: 0,
          ...(isPercentage
            ? {
                max: 100,
                interval: 10,
                splitLine: {show: true, lineStyle: {type: 'solid', color: GRID_LINE_COLOR}},
                axisLabel: {formatter: '{value}%'}
              }
            : {
                minInterval: 1,
                splitLine: {show: false},
                ...(yAxisMax !== undefined ? {max: yAxisMax} : {})
              })
        }
      ],
      series
    }
  }, [
    enabled,
    yearlyData,
    years,
    hierarchyEntries,
    t,
    difficultyFilter,
    effectiveVisibleIds,
    valueDisplayMode
  ])
}
